import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { getDefaultLocale } from '@src/i18n/locale.util'

dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(timezone)

// '12/22' => '2022-12-31'
export function getCardExpirationDateForOrder(date?: string) {
  if (!date) return ''
  return dayjs(date, 'MM/YY').format('YYYY-MM-DD')
}

// '1999-01-01' => true
export function getIsCardExpired(date?: string) {
  if (!date) return false
  const { month, year } = getCardExpirationMonthYear(date)
  const isExpired = dayjs().isAfter(dayjs(`${year}-${month}-01`))
  return isExpired
}

// '2022-12-31' => { month: '12', year: '2022' }
export function getCardExpirationMonthYear(date?: string) {
  if (!date) return { month: '', year: '' }
  const [year, month] = date.split('-')
  return { month, year }
}

export function formatDate(date: string, datePattern: string) {
  return dayjs(date).format(datePattern)
}

export function minutesToMilliseconds(minutes: number) {
  return minutes * 60 * 1000
}

export const secondsToMinutes = (seconds: number) => seconds / 60

export const secondsToMinutesRounded = (seconds: number) => {
  const minutes = secondsToMinutes(seconds)
  if (minutes > 0 && minutes < 1) return 1
  return Math.round(minutes)
}

/**
 * Get current time in a specific timezone as minutes since midnight (0-1439)
 * @param timezone - IANA timezone identifier (e.g., 'America/Los_Angeles')
 * @returns Minutes since midnight in the specified timezone
 */
export function getCurrentTimeInTimezone(timezone: string): number {
  const now = dayjs().tz(timezone)
  return now.hour() * 60 + now.minute()
}

/**
 * Get current day of week name in lowercase for a specific timezone
 * @param timezone - IANA timezone identifier (e.g., 'America/Los_Angeles')
 * @returns Day name in lowercase (e.g., 'monday', 'tuesday')
 */
export function getCurrentDayInTimezone(timezone: string): string {
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const now = dayjs().tz(timezone)
  return dayNames[now.day()]
}

/**
 * Format date and time in a specific timezone using dayjs
 * @param timezone - IANA timezone identifier (e.g., 'America/Los_Angeles')
 * @returns Formatted string like "Monday, Nov 3, 2025, 6:50 AM PST"
 */
export function formatDateTimeInTimezone(timezone: string): string {
  const now = dayjs().tz(timezone)

  // Format: "Monday, Nov 3, 2025, 6:50 AM PST"
  const dateTimeStr = now.format('dddd, MMM D, YYYY, h:mm A')

  // Get timezone abbreviation using Intl
  const tzFormatter = new Intl.DateTimeFormat(getDefaultLocale().code, {
    timeZone: timezone,
    timeZoneName: 'short',
  })
  const timezoneName =
    tzFormatter.formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value ?? ''

  return `${dateTimeStr} ${timezoneName}`
}

/**
 * Parse time string to minutes since midnight
 * Supports both 12-hour format (e.g., "6:00 AM", "3:00 PM") and 24-hour format (e.g., "09:00", "17:00")
 * @param timeStr - Time string to parse
 * @returns Minutes since midnight (0-1439)
 */
export function parseTimeToMinutes(timeStr: string): number {
  const trimmed = timeStr.trim()

  // Try to parse as 12-hour format (e.g., "6:00 AM", "3:00 PM")
  if (/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.test(trimmed)) {
    const parsed = dayjs(trimmed, 'h:mm A')
    if (parsed.isValid()) {
      return parsed.hour() * 60 + parsed.minute()
    }
  }

  // Try to parse as 24-hour format (e.g., "09:00", "17:00")
  if (/^(\d{1,2}):(\d{2})$/.test(trimmed)) {
    const parsed = dayjs(trimmed, 'HH:mm')
    if (parsed.isValid()) {
      return parsed.hour() * 60 + parsed.minute()
    }
  }

  // If parsing fails, return 0 (midnight)
  return 0
}
